import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workerContractsApi,
  WORKER_EVENT_TYPES,
  type WorkerEventDto,
} from '../../api/workerContracts';
import { personsApi } from '../../api/persons';
import type { PersonDto } from '../../types/person';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * P1b — Darbuotojų sutartys: the event-sourced contract register (legacy
 * darbuotojai_d). One row per contract (current-state projection); expand a
 * worker to see/edit the event timeline. Writes are native-only (ADR-004):
 * on a mirror workspace the register is read-only — changes happen in
 * Apskaita5 and arrive with the sync.
 */
export default function WorkerContracts() {
  const isMirror = useIsMirror();
  const queryClient = useQueryClient();
  const { data: contracts, isLoading, isError } = useQuery({
    queryKey: ['worker-contracts'],
    queryFn: workerContractsApi.list,
  });
  const { data: workers } = useQuery({
    queryKey: ['workers'],
    queryFn: () => personsApi.list({ type: 'worker', limit: 200 }),
    staleTime: 5 * 60 * 1000,
  });

  const [expanded, setExpanded] = useState<number | null>(null);
  const [timeline, setTimeline] = useState<WorkerEventDto[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const openTimeline = useCallback(async (workerId: number) => {
    setError('');
    try {
      setTimeline(await workerContractsApi.timeline(workerId));
      setExpanded(workerId);
    } catch (e: any) {
      setError(e.message || 'Nepavyko gauti įvykių');
    }
  }, []);

  const afterWrite = async () => {
    await queryClient.invalidateQueries({ queryKey: ['worker-contracts'] });
    if (expanded != null) await openTimeline(expanded);
  };

  useReportScreen(
    `Darbuotojų sutartys: ${contracts?.length ?? 0} sutarčių${isMirror ? ' (tik skaityti)' : ''}`,
    { recordCount: contracts?.length ?? 0 },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Darbuotojų sutartys</h1>
          <p className="text-sm text-gray-500">
            Sutarčių įvykiai — įdarbinimas, atleidimas, parametrų pokyčiai (rengia DUŽ žiniaraščių skaičiavimą).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/payroll"
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
          >
            ← Darbo užmokestis
          </Link>
          {!isMirror && (
            <button
              onClick={() => setShowForm(v => !v)}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              {showForm ? 'Uždaryti' : '+ Naujas įvykis'}
            </button>
          )}
        </div>
      </div>

      {isMirror && (
        <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti — sutartis keiskite Apskaita5 programoje.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!isMirror && showForm && (
        <AddEventForm workers={workers ?? []} onDone={async id => { setShowForm(false); await afterWrite(); if (expanded === null) await openTimeline(id); }} onError={setError} />
      )}

      {isError && <div className="error-message">Nepavyko įkelti sutarčių — atnaujinkite puslapį.</div>}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-50">
          {(contracts ?? []).map(c => (
            <div key={`${c.workerId}-${c.contractSerial}-${c.contractNumber}`}>
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => (expanded === c.workerId ? setExpanded(null) : openTimeline(c.workerId))}
              >
                <span className={`w-2 h-2 rounded-full ${c.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{c.workerName}</span>
                    <span className="text-xs text-gray-400">
                      Sutartis {c.contractSerial && c.contractSerial + ' '}{c.contractNumber}
                    </span>
                    {!c.isActive && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-500">
                        Atleistas {c.firedFrom?.slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Nuo {c.hiredFrom.slice(0, 10)}
                    {c.position ? ` · ${c.position}` : ''}
                    {` · ${c.wageType === 'v' ? 'valandinis' : 'etatinis'} ${c.contractWage}`}
                    {c.workLoad !== 1 ? ` · etatas ${(c.workLoad * 100).toFixed(0)}%` : ''}
                    {c.accumulatesPension ? ' · pensija 2+3' : ''}
                  </p>
                </div>
                <span className="text-xs text-gray-400">{expanded === c.workerId ? '▲' : '▼'}</span>
              </div>

              {expanded === c.workerId && timeline && (
                <div className="px-4 pb-3 bg-gray-50/50">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Įvykis</th>
                        <th>Reikšmė</th>
                        <th>Sutartis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeline.map(e => (
                        <tr key={e.id}>
                          <td>{e.effectiveFrom.slice(0, 10)}</td>
                          <td>{WORKER_EVENT_TYPES.find(t => t.code === e.eventType)?.label ?? e.eventType}</td>
                          <td>
                            {[e.amountValue != null ? String(e.amountValue) : '', e.textValue ?? '']
                              .filter(Boolean)
                              .join(' · ')}
                          </td>
                          <td>
                            {e.contractSerial} {e.contractNumber}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {(contracts ?? []).length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">
              Sutarčių įvykių nėra — pradėkite nuo įdarbinimo ('Įdarbinimas') įvykio.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** The single add-event form (one decision per submit — minimal UX law). */
function AddEventForm({
  workers,
  onDone,
  onError,
}: {
  workers: PersonDto[];
  onDone: (workerId: number) => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [workerId, setWorkerId] = useState<number | null>(null);
  const [eventType, setEventType] = useState('d');
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [contractNumber, setContractNumber] = useState('1');
  const [contractSerial, setContractSerial] = useState('');
  const [amountValue, setAmountValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [saving, setSaving] = useState(false);

  const type = WORKER_EVENT_TYPES.find(t => t.code === eventType)!;
  const isHire = eventType === 'd';

  useEffect(() => {
    if (eventType === 'u') setTextValue('p');
    else if (eventType !== 'f') setTextValue('');
  }, [eventType]);

  const submit = async () => {
    if (!workerId) {
      onError('Pasirinkite darbuotoją.');
      return;
    }
    setSaving(true);
    onError('');
    try {
      await workerContractsApi.addEvent({
        workerId,
        eventType,
        effectiveFrom,
        contractNumber: Number(contractNumber) || 1,
        contractSerial: contractSerial || null,
        amountValue: type.needsAmount && amountValue ? Number(amountValue) : null,
        textValue: type.needsText && textValue ? textValue : null,
      });
      await onDone(workerId);
    } catch (e: any) {
      onError(e.message || 'Nepavyko išsaugoti įvykio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', padding: '1rem' }}>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Darbuotojas</span>
        <select value={workerId ?? ''} onChange={e => setWorkerId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">— darbuotojas —</option>
          {workers.map(w => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Įvykis</span>
        <select value={eventType} onChange={e => setEventType(e.target.value)}>
          {WORKER_EVENT_TYPES.map(t => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Data</span>
        <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} />
      </label>
      <label className="text-sm">
        <span className="block text-gray-500 mb-1">Sutarties nr.</span>
        <input
          style={{ width: '5rem' }}
          value={contractNumber}
          onChange={e => setContractNumber(e.target.value)}
          disabled={!isHire}
          title={isHire ? '' : 'Numeris nustatomas pagal darbuotojo paskutinę sutartį'}
        />
      </label>
      {isHire && (
        <label className="text-sm">
          <span className="block text-gray-500 mb-1">Serija</span>
          <input style={{ width: '5rem' }} value={contractSerial} onChange={e => setContractSerial(e.target.value)} />
        </label>
      )}
      {type.needsAmount && (
        <label className="text-sm">
          <span className="block text-gray-500 mb-1">
            {eventType === 'u' ? 'Suma' : eventType === 'k' ? 'Etatas (0–1)' : 'Reikšmė'}
          </span>
          <input style={{ width: '7rem' }} value={amountValue} onChange={e => setAmountValue(e.target.value)} />
        </label>
      )}
      {type.needsText && (
        <label className="text-sm">
          <span className="block text-gray-500 mb-1">{eventType === 'u' ? 'Tipas (p/v)' : 'Tekstas'}</span>
          {eventType === 'u' ? (
            <select value={textValue || 'p'} onChange={e => setTextValue(e.target.value)}>
              <option value="p">Etatinis (p)</option>
              <option value="v">Valandinis (v)</option>
            </select>
          ) : (
            <input value={textValue} onChange={e => setTextValue(e.target.value)} />
          )}
        </label>
      )}
      <button onClick={submit} disabled={saving} className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
        {saving ? '…' : 'Išsaugoti'}
      </button>
    </div>
  );
}
